/**
 * Sibling-variant fallback for missing spec/variant fields.
 *
 * For variants in the same model that don't have a value for a given physical
 * field (dimensions, weight, towing, charging hardware, etc.), we borrow the
 * value from the sibling variant with the closest starting price and mark the
 * field as "estimated" so the UI can flag it.
 *
 * We intentionally do NOT borrow fields that genuinely differ per variant such
 * as battery_kwh, range_wltp_km, power_hp, torque_nm, consumption_wh_km or
 * acceleration_0_100.
 */

export interface Sibling {
  id: string;
  name: string;
  price: number | null;
  variant: Record<string, any>;
  specs: Record<string, any> | null;
}

export interface BorrowedField {
  field: string;
  label: string;
  fromName: string;
}

export interface FallbackResult<V, S> {
  dispVariant: V;
  dispSpecs: S;
  borrowed: BorrowedField[];
}

// Field -> Danish label shown in the banner
const VARIANT_FIELDS: Record<string, string> = {
  trunk_l: 'Bagagerum',
  curb_weight_kg: 'Egenvægt',
  tow_kg_braked: 'Trækvægt (bremset)',
  tow_kg_unbraked: 'Trækvægt (ubremset)',
  tow_bar_standard: 'Trækkrog (standard)',
  length_m: 'Længde',
  width_m: 'Bredde',
  height_m: 'Højde',
  wheelbase_m: 'Akselafstand',
  seats: 'Siddepladser',
  max_seats: 'Maks. sæder',
  doors: 'Døre',
  isofix_count: 'Isofix',
  isofix_three_in_second_row: '3 Isofix på 2. sæderække',
  turning_diameter_m: 'Venderadius',
};

const SPEC_FIELDS: Record<string, string> = {
  charging_ac_kw: 'AC-opladning',
  ac_faser: 'AC-faser',
  charging_dc_kw: 'DC-hurtigladning',
  dc_charge_10_80_min: 'Ladetid 10-80%',
  stiktype: 'Stiktype',
  ladestik_placering: 'Ladestik placering',
  batterikemi: 'Batterikemi',
  top_speed_kmh: 'Topfart',
};

function isMissing(v: any): boolean {
  return v === null || v === undefined || v === '';
}

function sortByPriceCloseness(siblings: Sibling[], price: number | null): Sibling[] {
  return [...siblings].sort((a, b) => {
    if (price == null) {
      // No reference price — prefer siblings that have a price, then any
      const ap = a.price == null ? Infinity : 0;
      const bp = b.price == null ? Infinity : 0;
      return ap - bp;
    }
    const ad = a.price == null ? Infinity : Math.abs(a.price - price);
    const bd = b.price == null ? Infinity : Math.abs(b.price - price);
    return ad - bd;
  });
}

export function applySiblingFallback<V extends Record<string, any>, S extends Record<string, any> | null>(
  variant: V,
  specs: S,
  currentPrice: number | null,
  siblings: Sibling[],
): FallbackResult<V, S extends null ? Record<string, any> : S> {
  const ordered = sortByPriceCloseness(siblings, currentPrice);

  const dispVariant: any = { ...variant };
  const dispSpecs: any = { ...(specs ?? {}) };
  const borrowed: BorrowedField[] = [];

  for (const [field, label] of Object.entries(VARIANT_FIELDS)) {
    if (!isMissing(dispVariant[field])) continue;
    for (const s of ordered) {
      const val = s.variant?.[field];
      if (!isMissing(val)) {
        dispVariant[field] = val;
        borrowed.push({ field, label, fromName: s.name });
        break;
      }
    }
  }

  for (const [field, label] of Object.entries(SPEC_FIELDS)) {
    if (!isMissing(dispSpecs[field])) continue;
    for (const s of ordered) {
      const val = s.specs?.[field];
      if (!isMissing(val)) {
        dispSpecs[field] = val;
        borrowed.push({ field, label, fromName: s.name });
        break;
      }
    }
  }

  return { dispVariant, dispSpecs, borrowed };
}
